import { siteApi } from "@api";
import { Paths } from "@constants";
import useUserMode from "@hooks/useUserMode";
import logger from "@utils/logger";
import { get } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const usePromotionBottomPromptVisibility = (isPromotionsClaimable: boolean) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { isGuest } = useUserMode();
  const [shouldShowPrompt, setShouldShowPrompt] = useState(false);
  const [withinPromotionLandedThreshold, setWithinPromotionLandedThreshold] = useState<boolean | null>(null);

  const { data: promotionStatusData } = siteApi.useGetRewardsStatusQuery(undefined, {
    skip: isGuest,
    refetchOnMountOrArgChange: true,
  });

  const [trackVisit] = siteApi.useTrackPromotionPageVisitMutation();

  const hiddenPages = useMemo(
    () => [
      Paths.PRIVATE.PRIZES,
      Paths.PRIVATE.PROMOTION_DAILY_LOGIN,
      Paths.PRIVATE.PROMOTION_CASHBACK,
      Paths.PRIVATE.PROMOTION_REFER_BALLS,
      Paths.PRIVATE.PROMOTION_PLAY_AND_SPIN,
      Paths.PRIVATE.PROMOTION_SHARE_AND_EARN,
      Paths.PRIVATE.PROMOTION_SECRET_CODE,
      Paths.PRIVATE.PROMOTION_REDEEM_SECRET_CODE,
      Paths.PRIVATE.PROMOTION_MEGA_SHARE,
    ],
    [],
  );

  const trackPageVisit = useCallback(async () => {
    try {
      if (isGuest) return;

      const result = await trackVisit();
      const threshold = get(result, "data.withinPromotionLandedThreshold");
      if (threshold !== undefined) {
        setWithinPromotionLandedThreshold(threshold);
      }
    } catch (error) {
      logger._console.error("Failed to track promotion page visit", error);
    }
  }, [isGuest, trackVisit]);

  useEffect(() => {
    const isPrizesPage = currentPath === Paths.PRIVATE.PRIZES;
    if (isPrizesPage) {
      trackPageVisit();
    }
  }, [currentPath, trackPageVisit]);

  useEffect(() => {
    if (isGuest) {
      setShouldShowPrompt(false);
      return;
    }

    const isOnHiddenPage = hiddenPages.includes(currentPath);
    if (isOnHiddenPage) {
      setShouldShowPrompt(false);
      return;
    }

    if (!isPromotionsClaimable) {
      setShouldShowPrompt(false);
      return;
    }

    const thresholdValue =
      withinPromotionLandedThreshold !== null
        ? withinPromotionLandedThreshold
        : get(promotionStatusData, "withinPromotionLandedThreshold", false);
    setShouldShowPrompt(thresholdValue);
  }, [isGuest, currentPath, hiddenPages, isPromotionsClaimable, promotionStatusData, withinPromotionLandedThreshold]);

  if (isGuest) {
    return {
      shouldShowPrompt: false,
    };
  }

  return {
    shouldShowPrompt,
  };
};

export default usePromotionBottomPromptVisibility;
